/**
 * Two V8 cards on the resident chart's Overview, with the design's own markup (V9
 * L3000-3006 "Resident devices", L2577-2592 "Veteran status") and only what the back end has:
 *
 *   - Devices: the resident's paired devices from GET /sensors/devices, connect after a
 *     consent check (the vendor's sign-in, or Junction's hosted page), and Remove. The
 *     building's own staff change them; every other role reads.
 *   - Veteran status: no route stores the answer yet, so the card asks nothing it cannot keep.
 *     It says the question has not been asked here, and offers no answer buttons.
 */
import type { ResidentDevices } from '@aithora/shared';
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;

const NAMES: Record<string, string> = { junction: 'Junction (any device)', oura: 'Oura', fitbit: 'Fitbit', withings: 'Withings', whoop: 'Whoop', garmin: 'Garmin', dexcom: 'Dexcom' };
export const deviceName = (p: string) => NAMES[p] ?? p.charAt(0).toUpperCase() + p.slice(1);
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export interface DevicesInput {
  data: ResidentDevices | null;
  /** The resident's name, for the consent question. */
  name: string;
  /** The building's own staff may connect and remove (V9: read-only roles never change care). */
  canAct: boolean;
  /** The Facility Admin sets a device type up in Settings, Integrations. */
  isAdmin: boolean;
  /** The provider waiting on the consent question, or null. */
  asking: string | null;
  busy: boolean;
  act: {
    ask: (provider: string | null) => void;
    connect: (provider: string) => void;
    remove: (provider: string) => void;
    openIntegrations: () => void;
  };
}

export function devicesVals(inp: DevicesInput) {
  const d = inp.data;
  const first = inp.name.split(' ')[0] || 'this resident';
  const connected = d?.connected ?? [];
  const paired = new Set(connected.map((c) => c.provider));
  const ingestOn = d?.ingestEnabled !== false;
  // What can connect here: each vendor set up on this server, and Junction when its key is in.
  const ready = d ? (ingestOn ? d.connectable : []).concat(d.junctionConfigured ? ['junction'] : []) : [];
  const left = ready.filter((p) => !paired.has(p));
  const lead = !d
    ? 'Loading.'
    : !ingestOn
      ? 'Device readings are not switched on yet, so nothing pairs. Aithora turns them on when the building is ready.'
      : ready.length === 0
        ? inp.isAdmin ? 'No devices are set up for this building yet. Set one up once in Settings › Integrations, then connect ' + first + ' here.' : 'No devices are set up for this building yet. Ask your Facility Admin to set one up in Settings.'
        : connected.length === 0
          ? 'Connect a device so ' + first + '’s readings reach Guardian and Today. Consent must be on file first.'
          : left.length ? 'Add another device below.' : 'Every device set up for this building is connected for ' + first + '.';
  return {
    rdvChip: connected.length ? connected.length + ' connected' : 'NONE YET',
    rdvChipStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.05em;' + (connected.length ? 'color:#12503C;background:rgba(47,138,111,.16);' : 'color:#54606C;background:rgba(34,39,46,.08);'),
    rdvMeta: ready.length ? ready.length + (ready.length === 1 ? ' device type' : ' device types') + ' set up for this building' : '',
    rdvRows: connected.map((c) => ({
      name: deviceName(c.provider),
      note: c.lastReadingAt ? 'last reading ' + when(c.lastReadingAt) : 'connected ' + when(c.connectedAt) + ' · no reading yet',
      canRemove: inp.canAct,
      onRemove: (e: Ev) => { pre(e); if (inp.canAct && !inp.busy) inp.act.remove(c.provider); },
    })),
    rdvLead: lead,
    rdvAsking: !!inp.asking && inp.canAct,
    rdvAskText: inp.asking ? 'Connect ' + deviceName(inp.asking) + ' for ' + inp.name + '? Their consent, or their representative’s, for sharing device data must be on file.' : '',
    rdvYes: (e: Ev) => { pre(e); if (inp.asking && !inp.busy) inp.act.connect(inp.asking); },
    rdvNo: (e: Ev) => { pre(e); inp.act.ask(null); },
    rdvAdd: !inp.canAct
      ? []
      : ready.length === 0
        ? inp.isAdmin && d ? [{ label: 'Open Integrations', onClick: (e: Ev) => { pre(e); inp.act.openIntegrations(); } }] : []
        : inp.asking ? [] : left.map((p) => ({ label: 'Connect ' + deviceName(p), onClick: (e: Ev) => { pre(e); inp.act.ask(p); } })),
  };
}

/** Veteran status (V8): no back end stores the answer yet, so nothing is asked here. */
export function veteranVals() {
  return {
    rVetOpts: [] as unknown[],
    rVetWho: 'Not asked yet',
    rVetYes: false,
    rVetItf: undefined,
    rVetItfLabel: '',
    rVetItfStyle: '',
    rVetDoc: undefined,
    rVetDocLabel: '',
  };
}
